<div class="page-container">
  <app-page-header
    title="Staff Directory"
    subtitle="View all staff members and their station assignments"
    [showBackButton]="true"
    backRoute="/userhome">
  </app-page-header>

  <div class="content-wrapper">
    <!-- Controls bar -->
    <div class="controls-bar">
      <!-- Search -->
      <input
        type="text"
        class="search-input"
        placeholder="Search by name or email..."
        (input)="onSearch($any($event.target).value)">

      <!-- Role filter -->
      <mat-form-field appearance="outline" class="role-filter-field" subscriptSizing="dynamic">
        <mat-label>Role</mat-label>
        <mat-select
          [(ngModel)]="selectedRole"
          (selectionChange)="onRoleFilter($event.value)"
          panelClass="owner-station-filter-panel">
          <mat-option *ngFor="let opt of roleOptions" [value]="opt.value">
            {{ opt.label }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <!-- View toggle -->
      <div class="view-toggle">
        <button
          type="button"
          [class.active]="viewMode === 'by-person'"
          (click)="switchView('by-person')">
          By Person
        </button>
        <button
          type="button"
          [class.active]="viewMode === 'by-station'"
          (click)="switchView('by-station')">
          By Station
        </button>
      </div>

      <!-- Total count -->
      <span class="total-count">
        {{ viewMode === 'by-station' ? totalStations + ' stations' : totalStaff + ' staff members' }}
      </span>
    </div>

    <div class="pagination-summary"
      *ngIf="viewMode === 'by-person' && totalStaff > 0 && !loading">
      Showing {{ paginationStart }}–{{ paginationEnd }}
      of {{ totalStaff }} staff members
    </div>

    <div class="pagination-summary"
      *ngIf="viewMode === 'by-station' && totalStations > 0 && !loading">
      Showing {{ stationsPaginationStart }}–{{ stationsPaginationEnd }}
      of {{ totalStations }} stations
    </div>

    <!-- Loading state -->
    <div *ngIf="loading" class="loading-state">
      Loading staff directory...
    </div>

    <!-- BY PERSON VIEW -->
    <div *ngIf="!loading && viewMode === 'by-person'">
      <div class="staff-card"
        *ngFor="let member of staff; trackBy: trackByUserId">

        <!-- Person header -->
        <div class="staff-card-header">
          <div class="staff-avatar">
            {{ member.firstname?.charAt(0) }}
            {{ member.lastname?.charAt(0) }}
          </div>
          <div class="staff-info">
            <div class="staff-name">
              {{ member.firstname }} {{ member.lastname }}
            </div>
            <div class="staff-email">{{ member.email }}</div>
          </div>
          <div class="staff-badge">
            <span class="station-count-badge">
              {{ member.station_count }}
              {{ member.station_count === 1 ? 'station' : 'stations' }}
            </span>
          </div>
        </div>

        <!-- Station assignments -->
        <div class="station-assignments">
          <div class="assignment-row"
            *ngFor="let assignment of member.station_assignments">

            <span class="station-name">
              {{ assignment.station_name }}
              <span class="call-sign">
                ({{ assignment.call_sign }})
              </span>
              <span class="station-location"
                *ngIf="assignment.city">
                · {{ assignment.city }}
                <span *ngIf="assignment.state">
                  , {{ assignment.state }}
                </span>
              </span>
            </span>

            <div class="assignment-right">
              <span *ngIf="assignment.owner_name"
                class="owner-group-label">
                via {{ assignment.owner_name }}
              </span>
              <span [class]="'role-badge ' + roleBadgeClass(assignment.role)">
                {{ roleBadgeLabel(assignment.role) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div class="empty-state"
        *ngIf="staff.length === 0 && !loading">
        <p>No staff members found matching your filters.</p>
      </div>

      <div class="pagination"
        *ngIf="totalPages > 1 && !loading"
        role="navigation">

        <button type="button"
          class="pagination-btn"
          [class.disabled]="currentPage === 1"
          [disabled]="currentPage === 1"
          (click)="goToPage(currentPage - 1)">
          Previous
        </button>

        <div class="pagination-numbers">
          <button type="button"
            *ngFor="let page of getPaginationPages()"
            class="pagination-number"
            [class.active]="page === currentPage"
            (click)="goToPage(page)">
            {{ page }}
          </button>
        </div>

        <button type="button"
          class="pagination-btn"
          [class.disabled]="currentPage === totalPages"
          [disabled]="currentPage === totalPages"
          (click)="goToPage(currentPage + 1)">
          Next
        </button>
      </div>
    </div>

    <!-- BY STATION VIEW -->
    <div *ngIf="!loading && viewMode === 'by-station'">
      <div class="station-card"
        *ngFor="let station of stationsSummary; trackBy: trackByStationId">

        <div class="station-card-header">
          <div class="station-title">
            {{ station.station_name }}
            <span class="call-sign">({{ station.call_sign }})</span>
            <span class="location"
              *ngIf="station.city">
              · {{ station.city }}
              <span *ngIf="station.state">
                , {{ station.state }}
              </span>
            </span>
          </div>
          <span class="staff-count-badge">
            {{ station.staff_count }} staff
          </span>
        </div>

        <div class="station-staff-list">
          <div class="station-staff-row"
            *ngFor="let person of station.staff">
            <span class="person-name">
              {{ person.firstname }} {{ person.lastname }}
            </span>
            <span class="person-email">{{ person.email }}</span>
            <span [class]="'role-badge ' + roleBadgeClass(person.role)">
              {{ roleBadgeLabel(person.role) }}
            </span>
          </div>
        </div>
      </div>

      <div class="empty-state"
        *ngIf="stationsSummary.length === 0 && !loading">
        <p>No stations found.</p>
      </div>

      <div class="pagination stations-pagination"
        *ngIf="stationsTotalPages > 1 && !loading"
        role="navigation">

        <button type="button"
          class="pagination-btn"
          [class.disabled]="stationsCurrentPage === 1"
          [disabled]="stationsCurrentPage === 1"
          (click)="goToStationsPage(stationsCurrentPage - 1)">
          Previous
        </button>

        <div class="pagination-numbers">
          <button type="button"
            *ngFor="let page of getPaginationPages(stationsCurrentPage, stationsTotalPages)"
            class="pagination-number"
            [class.active]="page === stationsCurrentPage"
            (click)="goToStationsPage(page)">
            {{ page }}
          </button>
        </div>

        <button type="button"
          class="pagination-btn"
          [class.disabled]="stationsCurrentPage === stationsTotalPages"
          [disabled]="stationsCurrentPage === stationsTotalPages"
          (click)="goToStationsPage(stationsCurrentPage + 1)">
          Next
        </button>
      </div>
    </div>
  </div>
</div>
